import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { get, isEmpty } from 'lodash';
import dayjs from 'utils/dayjs';

import { parseOtaData } from './competitorPricing/parser';
import { getLYSpitDateFor } from '../../helpers/dateHelper';

import session from 'containers/App/session';
import forecastModule from 'containers/App/data/endpoints/forecastByDay';
import currentStatsModule from 'containers/App/data/endpoints/currentRoomsRevenueByDay';
import lyFinalStatsModule from 'containers/App/data/endpoints/lyFinalRoomsRevenueByDay';
import lySpitStatsModule from 'containers/App/data/endpoints/lySpitRoomsRevenueByDay';
import budgetByDayModule from 'containers/App/data/endpoints/budgetByDay';
import y2FinalStatsModule from 'containers/App/data/endpoints/y2FinalRoomsRevenueByDay';
import y2SpitStatsModule from 'containers/App/data/endpoints/y2SpitRoomsRevenueByDay';
import otaInsightsDailyWithChangeModule from 'containers/App/data/endpoints/otaInsightsDailyWithChange';
import { FEATURES } from 'utils/permissions';
import { y2SpitOf } from 'utils/dates';

import DayViewBreadcrumb from './dayViewBreadcrumb';
import Spinner from '../UI/spinner';
import Widget from '../UI/widget';
import DateFilterCard from './dateFilterCard';
import TotalCard from './totalCard';
import CompetitorPricing from './competitorPricing/competitorPricing';
import StatusOverview from '../common/roomsAndRevenueOverview/statusOverview';

import BusinessAnalysis from 'containers/widgets/BusinessAnalysis';
import OvernightStats from 'containers/widgets/OvernightStats';
import BusinessStatus from 'containers/widgets/BusinessStatus';
import InventoryBlocks from 'containers/widgets/InventoryBlocks';

import './dayView.style.css';
import NotesManagement from 'containers/shared/NotesManagement/NotesManagement';
import EventsManagement from 'containers/shared/EventsManagement/EventsManagement';
import { getEventsForRange } from 'legacy/main/actions/dayViewActions';
import { Col, Row } from 'antd';

const DayView = () => {
    const apiDateFormat = 'YYYY-MM-DD';

    const propertyId = useSelector(session.selectors.selectSelectedPropertyId);
    const currency = useSelector(session.selectors.selectCurrency);
    const accessibleFeatures = useSelector(session.selectors.selectAccessibleFeatures);
    const forecast = useSelector(forecastModule.selectors.selectState);
    const currentStats = useSelector(currentStatsModule.selectors.selectState);
    const budget = useSelector(budgetByDayModule.selectors.selectState);
    const lySpitStats = useSelector(lySpitStatsModule.selectors.selectState);
    const lyFinalStats = useSelector(lyFinalStatsModule.selectors.selectState);
    const y2FinalStats = useSelector(y2FinalStatsModule.selectors.selectState);
    const y2SpitStats = useSelector(y2SpitStatsModule.selectors.selectState);
    const otaInsightsByDay = useSelector(otaInsightsDailyWithChangeModule.selectors.selectState);
    const otaCompsets = useSelector(session.selectors.selectOtaCompsets);
    const otaHotelId = useSelector(session.selectors.selectOtaHotelId);
    const eventsState = useSelector((state) => state.dayData?.events);
    const dayData = useSelector((state) => state.dayData);

    const isEventsLoading = dayData?.apiLoading?.isEventsLoading || false;
    const events = eventsState?.payload || [];

    const dispatch = useDispatch();
    const location = useLocation();

    const selectedDate = get(location, 'state.selectedDate');
    const [date, setDate] = useState(selectedDate ? dayjs(selectedDate, 'YYYY-MM-DD') : dayjs());
    const [datesRange, setDatesRange] = useState({});
    const [dayStats, setDayStats] = useState({});
    const [lySpitDayStats, setLySpitDayStats] = useState({});
    const [dayComparison, setDayComparison] = useState(true);

    const onDayOrDateComparisonChange = (dayComparisonValue) => {
        setDayComparison(dayComparisonValue);
    };

    const getSurroundingDateRanges = () => {
        const day = date.format(apiDateFormat);
        const lySpitDate = getLYSpitDateFor(date, dayComparison).format(apiDateFormat);
        const minus3Days = date.clone().subtract(3, 'd').format(apiDateFormat);
        const plus3Days = date.clone().add(3, 'd').format(apiDateFormat);

        return {
            day,
            lySpitDate,
            minus3Days,
            minus3DayLySpit: getLYSpitDateFor(dayjs(minus3Days), dayComparison).format(apiDateFormat),
            plus3Days,
            plus3DayLySpit: getLYSpitDateFor(dayjs(plus3Days), dayComparison).format(apiDateFormat),
            y2SpitDate: y2SpitOf(day, dayComparison),
            minus3DayY2Spit: y2SpitOf(minus3Days, dayComparison),
            plus3DayY2Spit: y2SpitOf(plus3Days, dayComparison),
            y2SpitToday: dayjs(y2SpitOf(dayjs(), dayComparison)).endOf('day').format(apiDateFormat),
        };
    };

    const fetchEvents = (day) => {
        dispatch(getEventsForRange(propertyId, day, day));
    };

    const getApiData = () => {
        dispatch(currentStatsModule.actions.resetState());
        dispatch(lyFinalStatsModule.actions.resetState());
        dispatch(lySpitStatsModule.actions.resetState());
        dispatch(y2FinalStatsModule.actions.resetState());
        dispatch(y2SpitStatsModule.actions.resetState());
        dispatch(budgetByDayModule.actions.resetState());
        dispatch(otaInsightsDailyWithChangeModule.actions.resetState());
        dispatch(forecastModule.actions.resetState());
        const { day, lySpitDate, y2SpitDate, minus3Days, plus3Days, minus3DayLySpit, plus3DayLySpit, y2SpitToday } =
            getSurroundingDateRanges();
        setDatesRange({
            startDate: day,
            endDate: day,
            lySpitStartDate: lySpitDate,
            lySpitEndDate: lySpitDate,
            y2SpitStartDate: y2SpitDate,
            y2SpitEndDate: y2SpitDate,
            minus3Days,
            plus3Days,
            minus3DayLySpit,
            plus3DayLySpit,
        });
        if (day && minus3Days && minus3DayLySpit && minus3Days) {
            dispatch(currentStatsModule.actions.fetch(propertyId, minus3Days, plus3Days));
            dispatch(lyFinalStatsModule.actions.fetch(propertyId, minus3DayLySpit, plus3DayLySpit));
            dispatch(lySpitStatsModule.actions.fetch(propertyId, minus3DayLySpit, plus3DayLySpit));
            dispatch(budgetByDayModule.actions.fetch(propertyId, minus3Days, plus3Days));
            dispatch(otaInsightsDailyWithChangeModule.actions.fetch(propertyId, day));
            dispatch(y2FinalStatsModule.actions.fetch(propertyId, y2SpitDate, y2SpitDate));
            dispatch(y2SpitStatsModule.actions.fetch(propertyId, y2SpitDate, y2SpitDate, y2SpitToday));

            if (dayjs(plus3Days).isBefore(dayjs(), 'day')) {
                dispatch(forecastModule.actions.resetState());
            } else {
                dispatch(
                    forecastModule.actions.fetch(
                        propertyId,
                        dayjs(minus3Days).isBefore(dayjs(), 'day') ? dayjs().format(apiDateFormat) : minus3Days,
                        plus3Days,
                    ),
                );
            }
            fetchEvents(day);
        }
    };

    const onDateChange = (newDate) => {
        setDate(dayjs(newDate));
    };

    // filter the ota data by the default compset
    const filterDataByDefaultCompset = () => {
        const otaData = otaInsightsByDay.data;
        if (otaData && !isEmpty(otaCompsets)) {
            const selectedCompSetIds = otaCompsets
                .find((set) => set.default)
                .competitors.map((competitor) => competitor.id);
            if (otaHotelId && !selectedCompSetIds.includes(Number(otaHotelId))) {
                selectedCompSetIds.push(Number(otaHotelId));
            }
            const compSet = otaData.filter((item) => selectedCompSetIds.includes(item.hotelId));
            return { date, payload: compSet };
        }
        return otaData;
    };

    useEffect(() => {
        getApiData();
        return () => {
            // on unmount
            dispatch(currentStatsModule.actions.resetState());
            dispatch(lyFinalStatsModule.actions.resetState());
            dispatch(lySpitStatsModule.actions.resetState());
            dispatch(y2FinalStatsModule.actions.resetState());
            dispatch(y2SpitStatsModule.actions.resetState());
            dispatch(budgetByDayModule.actions.resetState());
            dispatch(otaInsightsDailyWithChangeModule.actions.resetState());
            setDatesRange({});
            setDayStats({});
            setLySpitDayStats({});
        };
    }, [propertyId, date, dayComparison]);

    const canViewInventoryBlocks = accessibleFeatures.includes(FEATURES.inventoryBlocks);
    const canViewRoomForecast = accessibleFeatures.includes(FEATURES.roomForecast);
    const canViewRevenueForecast = accessibleFeatures.includes(FEATURES.revenueForecast);
    const canViewOTAInsights = accessibleFeatures.includes(FEATURES.otaInsights);
    const canViewNotes = accessibleFeatures.includes(FEATURES.notes);
    const canViewEvents = accessibleFeatures.includes(FEATURES.events);

    const isRoomRevenueChangesLoading =
        currentStats.fetching || lyFinalStats.fetching || lySpitStats.fetching || forecast.fetching || budget.fetching;
    const isStatsByDayLoaded = currentStats.fetched && lySpitStats.fetched;

    useEffect(() => {
        if (isStatsByDayLoaded) {
            setDayStats(
                currentStats.data.find((row) => row.stayDate === datesRange.startDate) || {
                    rooms: { guaranteed: 0 },
                    revenue: { accommodation: { guaranteed: 0 } },
                    inventory: { availableRoomCount: 0 },
                },
            );
            setLySpitDayStats(
                lySpitStats.data.find((row) => row.stayDate === datesRange.lySpitStartDate) || {
                    rooms: { guaranteed: 0 },
                    revenue: { accommodation: { guaranteed: 0 } },
                },
            );

            // Bypass scenario where data is fetched and loaded from previous page (e.g. month view) and was not yet re-fetched by dayview Data was loading on previous page and did not have time yet
            // if (!dayStats) isStatsByDayLoading = true;
        }
    }, [isStatsByDayLoaded]);

    return (
        <>
            <DateFilterCard onDateSelectChange={onDateChange} selectedDate={date} dayComparisonValue={dayComparison} onDayOrDateComparisonChange={onDayOrDateComparisonChange} />
            <Row>
                <Col span={22} offset={1}>
                    <DayViewBreadcrumb dateChanged={onDateChange} currentDate={date} propertyId={propertyId} />

                    <div className="DayViewRow">
                        <Widget title="Performance">
                            {!isStatsByDayLoaded || isEmpty(dayStats) ? (
                                <Spinner />
                            ) : (
                                <TotalCard current={dayStats} lySpit={lySpitDayStats} currency={currency} />
                            )}
                        </Widget>
                    </div>

                    <div className="DayViewRow">
                        <Widget title="Room status overview">
                            {isRoomRevenueChangesLoading ? (
                                <Spinner />
                            ) : (
                                <StatusOverview
                                    type="Rooms"
                                    current={currentStats.data}
                                    lyFinal={lyFinalStats.data}
                                    lySpit={lySpitStats.data}
                                    budget={budget.data}
                                    forecast={forecast.data}
                                    canViewForecast={canViewRoomForecast}
                                    currency={currency}
                                    dayComparison={dayComparison}
                                />
                            )}
                        </Widget>
                        <Widget title="Revenue status overview">
                            {isRoomRevenueChangesLoading ? (
                                <Spinner />
                            ) : (
                                <StatusOverview
                                    type="Revenue"
                                    current={currentStats.data}
                                    lyFinal={lyFinalStats.data}
                                    lySpit={lySpitStats.data}
                                    budget={budget.data}
                                    forecast={forecast.data}
                                    canViewForecast={canViewRevenueForecast}
                                    currency={currency}
                                    dayComparison={dayComparison}
                                />
                            )}
                        </Widget>
                    </div>

                    <div className="DayViewRow">
                        {isRoomRevenueChangesLoading ? (
                            <Spinner />
                        ) : (
                            <BusinessStatus
                                filters={{
                                    startDate: datesRange.startDate,
                                    endDate: datesRange.endDate,
                                }}
                                displayFilters={datesRange}
                                currency={currency}
                                propertyId={propertyId}
                                currentStats={currentStats}
                                lyFinalStats={lyFinalStats}
                                lySpitStats={lySpitStats}
                                y2FinalStats={y2FinalStats}
                                y2SpitStats={y2SpitStats}
                                retryFunc={getApiData}
                                dayComparison={dayComparison}
                            />
                        )}
                    </div>
                    <div className="DashboardRow">
                        {canViewOTAInsights && (
                            <Widget title="Competitor Pricing">
                                {otaInsightsByDay.fetching || !otaInsightsByDay.fetched ? (
                                    <Spinner />
                                ) : (
                                    <CompetitorPricing
                                        currentDate={date}
                                        {...parseOtaData(filterDataByDefaultCompset())}
                                    />
                                )}
                            </Widget>
                        )}
                        {canViewInventoryBlocks ? (
                            <InventoryBlocks propertyId={propertyId} currency={currency} filters={datesRange} />
                        ) : (
                            <div style={{ flex: 1 }} />
                        )}
                    </div>

                    <div className="DayViewRow">
                        {isEmpty(datesRange) ? (
                            <Spinner />
                        ) : (
                            <OvernightStats propertyId={propertyId} filters={datesRange} />
                        )}
                        <div style={{ flex: 1 }} />
                    </div>

                    <div className="DayViewRow">
                        {isEmpty(datesRange) ? (
                            <Spinner />
                        ) : (
                            <BusinessAnalysis propertyId={propertyId} currency={currency} filters={datesRange} />
                        )}
                    </div>
                    {canViewNotes && (
                        <div className="DayViewRow">
                            <Widget title="Notes">
                                {isEmpty(datesRange) ? (
                                    <Spinner />
                                ) : (
                                    <NotesManagement
                                        start={dayjs(datesRange.day).format('YYYY-MM-DD')}
                                        end={dayjs(datesRange.day).format('YYYY-MM-DD')}
                                    />
                                )}
                            </Widget>
                        </div>
                    )}
                    {canViewEvents && (
                        <div className="DayViewRow">
                            <Widget title="Events">
                                <div style={{ padding: '20px' }}>
                                    {isEventsLoading ? (
                                        <Spinner />
                                    ) : (
                                        <EventsManagement events={events} refreshEvents={fetchEvents} />
                                    )}
                                </div>
                            </Widget>
                        </div>
                    )}
                </Col>
            </Row>
        </>
    );
};

export default DayView;
